import React, {RefObject, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {api, ApiContext} from '../../api';
import {Dialog} from '../Dialog';
import {Spinner} from '../Spinner';
import {UploadForm} from '../../components/UploadForm';
import {useIntl} from 'react-intl';

type Props = {
  ctx: ApiContext;
  shown: boolean;
  appId?: string;
  fileTypes?: string[];
  title?: string;
  onClose: () => void;
  onComplete: () => void;
  minWidth?: string;
};

type Form = {
  title: string;
  message: string;
  executeButtonLabel: string;
};

export function UploadDialog(props: Props): JSX.Element | null {
  const intl = useIntl();
  const [form, setForm] = useState<Form | undefined>();
  const ref = useRef<Dialog>(null);

  useEffect(() => {
    if (ref && ref.current && props.shown) {
      ref.current.showDialog();

      (async () => {
        try {
          const form = await api.getJson(
            props.ctx,
            `/app/${props.appId}/upload/new`,
          );
          setForm(form);
        } catch (e) {}
      })();
    }
  }, [ref, props.shown, props.ctx, props.appId]);

  return (
    <Dialog
      ref={ref}
      modal={true}
      title={
        form?.title ||
        props.title ||
        intl.formatMessage({
          id: 'Upload.Form.Title',
          defaultMessage: 'Upload',
        })
      }
      maxWidth="100%">
      <UploadDialogContents {...props} componentRef={ref} form={form} />
    </Dialog>
  );
}

type ContentProps = {
  ctx: ApiContext;
  componentRef: RefObject<Dialog>;
  appId?: string;
  fileTypes?: string[];
  onClose: () => void;
  onComplete: () => void;
  minWidth?: string;
  form?: Form;
};

function UploadDialogContents(props: ContentProps): JSX.Element | null {
  const intl = useIntl();

  if (!props.appId) {
    return <Spinner />;
  }

  return (
    <DialogContents style={{minWidth: props.minWidth}}>
      <UploadForm
        ctx={props.ctx}
        appId={props.appId}
        fileTypes={props.fileTypes}
        cancelLabel={intl.formatMessage({
          id: 'Action.Close',
          defaultMessage: 'Close',
        })}
        onCancel={() => {
          props.componentRef.current!.closeDialog();
          props.onClose();
        }}
        onComplete={props.onComplete}
        message={props.form?.message}
        executeButtonLabel={props.form?.executeButtonLabel}
      />
    </DialogContents>
  );
}

const DialogContents = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  padding-bottom: 1rem;
`;
